import { z } from 'zod';

export const ApprovalDigestActionType = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  REQUEST_INFO: 'REQUEST_INFO',
} as const;
export type ApprovalDigestActionType =
  (typeof ApprovalDigestActionType)[keyof typeof ApprovalDigestActionType];

export const ApprovalDigestActionStatus = {
  APPLIED: 'APPLIED',
  SKIPPED: 'SKIPPED',
  FAILED: 'FAILED',
} as const;
export type ApprovalDigestActionStatus =
  (typeof ApprovalDigestActionStatus)[keyof typeof ApprovalDigestActionStatus];

export const ApprovalDigestBillStepSchema = z.object({
  billId: z.string().uuid(),
  stepInstanceId: z.string().uuid(),
  code: z.string().min(1),
});

export type ApprovalDigestBillStep = z.infer<typeof ApprovalDigestBillStepSchema>;

export const ApprovalDigestPayloadSchema = z.object({
  tenantId: z.string().uuid(),
  inboxId: z.string().uuid(),
  entityId: z.string().uuid(),
  userId: z.string().uuid(),
  digestMessageId: z.string().uuid(),
  issuedAt: z.string(),
  expiresAt: z.string(),
  billSteps: z.array(ApprovalDigestBillStepSchema),
});

export type ApprovalDigestPayload = z.infer<typeof ApprovalDigestPayloadSchema>;

export const ApprovalDigestApplyActionSchema = z.object({
  billId: z.string().uuid(),
  stepInstanceId: z.string().uuid(),
  action: z.nativeEnum(ApprovalDigestActionType),
  comment: z.string().optional(),
});

export type ApprovalDigestApplyAction = z.infer<typeof ApprovalDigestApplyActionSchema>;

// What the worker hands the API for one digest reply; parsed where it crosses the queue.
export const ApprovalDigestApplyActionsRequestSchema = z.object({
  tenantId: z.string().uuid(),
  digestMessageId: z.string().uuid(),
  token: z.string().min(1),
  fromAddress: z.string().min(1),
  actions: z.array(ApprovalDigestApplyActionSchema),
});

export type ApprovalDigestApplyActionsRequest = z.infer<
  typeof ApprovalDigestApplyActionsRequestSchema
>;

export interface ApprovalDigestApplyActionResult {
  billId: string;
  stepInstanceId: string;
  action: ApprovalDigestActionType;
  status: ApprovalDigestActionStatus;
  reason?: string;
}

export interface ApprovalDigestApplyActionsResponse {
  results: ApprovalDigestApplyActionResult[];
}

export const ApprovalDigestReplyIntent = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  REQUEST_INFO: 'REQUEST_INFO',
  MIXED: 'MIXED',
  NO_ACTION: 'NO_ACTION',
} as const;
export type ApprovalDigestReplyIntent =
  (typeof ApprovalDigestReplyIntent)[keyof typeof ApprovalDigestReplyIntent];
export interface ApprovalDigestReplyBillContext {
  billId: string;
  stepInstanceId: string;
  code: string;
  payeeName?: string | null;
  totalAmount?: string | null;
  dueDate?: string | null;
  description?: string | null;
}

export interface ApprovalDigestReplyInput {
  replyText: string;
  replyHtml?: string;
  bills: ApprovalDigestReplyBillContext[];
}

export const ApprovalDigestReplyActionSchema = z.object({
  code: z.string(),
  action: z.nativeEnum(ApprovalDigestActionType),
  comment: z.string().optional(),
});

export type ApprovalDigestReplyAction = z.infer<typeof ApprovalDigestReplyActionSchema>;

export const ApprovalDigestReplySchema = z.object({
  intent: z.nativeEnum(ApprovalDigestReplyIntent),
  actions: z.array(ApprovalDigestReplyActionSchema),
  confidence: z.number().min(0).max(1),
  needsClarification: z.boolean(),
  clarificationQuestion: z.string().optional(),
});

export type ApprovalDigestReply = z.infer<typeof ApprovalDigestReplySchema>;
